import { useEffect, useMemo, useRef, useState } from 'react'
import { ArchiveRestore, ArrowDown, Copy, Gauge, PauseCircle, Pencil, Plus, ShieldCheck, Ticket, Trash2, X } from 'lucide-react'
import { Modal, RowMenu, Select } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import { guardedClose, useSaveBar } from '../shell/editing.js'
import { ajouterTarif, gesteTarif, modifierTarif } from './soirees-maison-api.js'
import { usePreferencesBilletterie, useTvaDefautBilletterie } from './soirees-maison-hooks.js'
import {
  CANAL_LIBELLE, PHRASE_PREMIER_BILLET_TARIF,
  demandeIdentite, etatBouton, euros, lectureSeule, libelleChoixHeure, phraseAjoutEnLigne, phraseCascadeAjout,
  phraseEffets, phraseGesteTarif, raisonsVerrousTarif, resumeLigneTarif, tarifPrecedent, tarifsOuverts, tarifsParCanal, tonVente, tvaLisible, champLisible,
  type Ambiguite, type Geste, type NomGeste, type Tarif,
} from './soirees-maison-model.js'
import {
  avertissementsSaisie, cascadesARejoindre, champDeLaSaisie, champsModifies, corpsTarif, saisieCopieDe, saisieDepuisTarif, saisieVierge,
  type ErreursTarif, type SaisieTarif,
} from './tarif-saisie-model.js'
import { tarifDepuisModele, tarifEffectif } from './assistant-soiree-model.js'
import { TarifFenetreChamps } from './TarifFenetreChamps.js'
import { Jauge } from './FicheVentes.js'
import type { Ecrivain } from './SoireeMaisonPage.js'

/**
 * L'ONGLET TARIFS — chaque tarif dit ce qui se passe VRAIMENT à la porte
 * (`vente_reelle`), ses compteurs et ce qui est enregistré sans effet.
 *
 * Retirer et supprimer restent des gestes de PRÉPARATION, sans confirmation
 * d'identité, quel que soit l'état de la soirée : le frein doit rester rapide
 * en pleine nuit. Leurs effets sur la cascade sont dits AVANT le clic, par la
 * phrase imposée (« Retirer SUR PLACE AVANT 2H30 met SUR PLACE (10,00 €) en
 * vente tout de suite. »).
 *
 * Ajouter et modifier passent par une MODALE (tâche focalisée) servie par la
 * Save Bar ; sur une soirée en ligne, ils demandent le droit de mise en vente
 * et une confirmation d'identité (principe 8).
 *
 * RANGÉ COMME CHEZ SHOTGUN (23/10/2026) : la jauge et le nombre de tarifs en
 * vente en tête ; les tarifs par canal, une ligne serrée chacun (nom et
 * quand, état, prix, vendus et jauge du quota) ; une cascade se lit d'un
 * tarif à l'autre (« prend le relais quand… ») ; les gestes dans un menu
 * « … » (modifier, dupliquer, retirer, supprimer), chacun grisé avec sa
 * raison. La modale d'un tarif s'ouvre au centre (« une magnifique pop-up
 * au centre », l'exploitant) ; « Dupliquer » ouvre un NOUVEAU tarif, copie
 * de l'autre, que la barre enregistre.
 */

export function FicheTarifs({ ecrivain, versLaSoiree }: { ecrivain: Ecrivain; versLaSoiree: () => void }) {
  const confirm = useConfirm()
  const { soiree: s, fiche } = ecrivain
  // La dernière édition ouverte reste montée le temps que la modale se referme.
  const [edition, setEdition] = useState<Edition | null>(null)
  const [ouverte, setOuverte] = useState(false)
  const [envoi, setEnvoi] = useState<string | null>(null)
  const ro = lectureSeule(fiche.actions)
  const etat = (nom: NomGeste, g: Geste) => etatBouton(nom, g, fiche.actions, fiche.ecriture)
  const ajout = etat('ajouter_tarif', s.gestes.ajouter_tarif)
  // Le taux que prendra un tarif créé sans taux : lu une fois, chez la billetterie.
  const tvaDefautBp = useTvaDefautBilletterie()
  const ouvrir = (tarifId: string | null, copieDe: string | null = null) => { setEdition((e) => ({ tarifId, copieDe, tour: (e?.tour ?? 0) + 1 })); setOuverte(true) }

  const geste = async (t: Tarif, g: 'retirer' | 'remettre' | 'supprimer') => {
    const verbe = g === 'retirer' ? 'Retirer' : g === 'remettre' ? 'Remettre' : 'Supprimer'
    const effets = phraseEffets(verbe, t.nom, t.gestes[g].effets)
    const detailsConfirm = [
      ...(effets ? [{ label: 'Effet sur les autres tarifs', value: effets }] : []),
      ...(demandeIdentite(g === 'remettre' ? 'remettre_tarif' : g === 'retirer' ? 'retirer_tarif' : 'supprimer_tarif', t.gestes[g])
        ? [{ label: 'Identité', value: 'Cette soirée est en ligne : Gestion vous demandera de confirmer votre identité.' }] : []),
    ]
    const ok = await confirm({
      title: g === 'retirer' ? `Retirer « ${t.nom} » de la vente ?` : g === 'remettre' ? `Remettre « ${t.nom} » en vente ?` : `Supprimer « ${t.nom} » ?`,
      message: phraseGesteTarif(g, t, s.statut),
      details: detailsConfirm,
      variant: g === 'supprimer' ? 'critical' : g === 'remettre' ? 'sensitive' : 'standard',
      confirmLabel: g === 'retirer' ? 'Retirer de la vente' : g === 'remettre' ? 'Remettre en vente' : 'Supprimer le tarif',
    })
    if (!ok) return
    setEnvoi(t.id)
    try {
      const e = await ecrivain.executer({
        ecran: `tarif:${t.id}:${g}`, corps: { version: t.version },
        envoi: (cle) => gesteTarif(s.id, t.id, g, t.version, cle),
        succes: (r) => (r?.inchange
          ? `Rien n’a changé : ${t.nom} était déjà ${g === 'retirer' ? 'retiré de la vente' : 'en vente'}.`
          : g === 'retirer' ? `${t.nom} retiré de la vente.` : g === 'remettre' ? `${t.nom} remis en vente.` : `${t.nom} supprimé.`),
        preparationHorsLigne: g === 'remettre' && !t.gestes.remettre.publier_requis,
      })
      ecrivain.signalerErreur(e)
    } finally { setEnvoi(null) }
  }


  const groupes = tarifsParCanal(s.tarifs)
  const n = s.tarifs.length

  return (
    <section className="bs-section bs-onglet-tarifs" aria-labelledby="bs-tarifs-titre">
      <div className="bs-section__tete">
        <h2 className="bs-h2" id="bs-tarifs-titre">Tarifs <span className="bs-h2__compte">· {n}</span></h2>
        {!ro ? (
          <span className="bs-geste bs-geste--droite">
            <button type="button" className="lc-btn lc-btn--primary" disabled={!ajout.actif} onClick={() => ouvrir(null)}><Plus size={15} aria-hidden="true" /> Ajouter un tarif</button>
            {ajout.raison ? <span className="lc-hint bs-geste__raison">{ajout.raison}</span> : null}
          </span>
        ) : null}
      </div>

      <div className="bs-tuiles">
        <div className="bs-tuile">
          <span className="bs-tuile__icone" aria-hidden="true"><Gauge size={18} /></span>
          <span className="bs-tuile__textes">
            <span className="bs-tuile__libelle">Jauge de la soirée</span>
            <span className="bs-tuile__valeur"><strong>{s.compteurs.places_occupees.toLocaleString('fr-FR')}</strong> / {s.jauge_totale.toLocaleString('fr-FR')} places</span>
          </span>
          {!ro ? <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={versLaSoiree}>Modifier</button> : null}
        </div>
        <div className="bs-tuile bs-tuile--vente">
          <span className="bs-tuile__icone" aria-hidden="true"><Ticket size={18} /></span>
          <span className="bs-tuile__textes">
            <span className="bs-tuile__libelle">En vente</span>
            <span className="bs-tuile__valeur"><strong>{tarifsOuverts(s.tarifs)}</strong> / {n} tarif{n > 1 ? 's' : ''}</span>
          </span>
        </div>
      </div>

      {n === 0 ? <p className="gd-calm">Aucun tarif : la soirée ne pourra pas être mise en vente tant qu’il n’y en a pas au moins un, actif.</p> : null}
      {groupes.map((g) => (
        <section key={g.canal} className="bs-groupe-tarifs" aria-labelledby={`bs-gt-${g.canal}`}>
          <div className="bs-groupe-tarifs__tete">
            <h3 className="bs-groupe-tarifs__titre" id={`bs-gt-${g.canal}`}>{g.titre}</h3>
            <span className="bs-groupe-tarifs__aide">{g.aide}</span>
          </div>
          <ul className="bs-tarifs">
            {g.tarifs.map((t) => {
              const ton = tonVente(t.vente_reelle.code)
              const mod = etat('modifier_tarif', t.gestes.modifier)
              const bascule = t.actif ? etat('retirer_tarif', t.gestes.retirer) : etat('remettre_tarif', t.gestes.remettre)
              const sup = etat('supprimer_tarif', t.gestes.supprimer)
              const precedent = tarifPrecedent(t, s.tarifs)
              // Le rang ne se montre que dans une vraie cascade (au moins deux tarifs du même groupe).
              const enCascade = !!t.cascade_groupe && t.cascade_rang !== null && s.tarifs.some((x) => x.id !== t.id && x.cascade_groupe === t.cascade_groupe)
              const occupe = envoi !== null
              return (
                <li key={t.id} className="bs-tarif-bloc">
                  {precedent ? (
                    <p className="bs-relais"><ArrowDown size={14} aria-hidden="true" /> Prend le relais quand « {precedent.nom} » n’est plus en vente</p>
                  ) : null}
                  <article className={`bs-tarif bs-tarif--${t.canal === 'SUR_PLACE' ? 'place' : 'ligne'}${t.actif ? '' : ' bs-tarif--retire'}`} aria-label={t.nom}>
                    <span className="bs-tarif__rang" aria-hidden={!enCascade}>{enCascade ? t.cascade_rang : ''}</span>
                    <span className="bs-tarif__ident">
                      <strong className="bs-tarif__libelle">{t.nom}</strong>
                      <span className="bs-tarif__sous">{resumeLigneTarif(t, s)}</span>
                    </span>
                    <span className="bs-tarif__etat">
                      {/* « Brouillon : rien ne se vend » vaut pour toute la soirée : la colonne de droite le dit une fois. */}
                      {t.vente_reelle.code !== 'SOIREE_PAS_EN_VENTE' ? (
                        <span className={`lc-pill lc-pill--${ton}`}><span className={`lc-dot${ton === 'idle' ? '' : ` lc-dot--${ton}`}`} aria-hidden="true" />{t.vente_reelle.libelle}</span>
                      ) : null}
                    </span>
                    <span className={`bs-tarif__prix${t.prix_cents === 0 ? ' bs-tarif__prix--mot' : ''}`}>
                      <strong>{t.prix_cents === 0 ? (t.exoneration ? 'Exonéré' : 'Gratuit') : euros(t.prix_cents)}</strong>
                      {t.frais_service_cents ? <span className="lc-hint">+ {euros(t.frais_service_cents)} de frais</span> : null}
                    </span>
                    <span className="bs-tarif__ventes">
                      <span className="bs-tarif__compte">
                        <strong>{t.compteurs.vendus.toLocaleString('fr-FR')}</strong>
                        {t.quota !== null ? ` / ${t.quota.toLocaleString('fr-FR')}` : ` vendu${t.compteurs.vendus > 1 ? 's' : ''} · sans quota`}
                      </span>
                      {t.quota !== null ? <Jauge valeur={t.compteurs.vendus} max={t.quota} libelle={`${t.nom} : billets vendus sur le quota`} fine /> : null}
                    </span>
                    {!ro ? (
                      <span className="bs-tarif__menu">
                        <RowMenu label={`Actions du tarif ${t.nom}`} items={[
                          { label: 'Modifier', icon: <Pencil size={15} aria-hidden="true" />, onClick: () => ouvrir(t.id), disabled: !mod.actif || occupe, reason: mod.raison ?? undefined },
                          { label: 'Dupliquer', icon: <Copy size={15} aria-hidden="true" />, onClick: () => ouvrir(null, t.id), disabled: !ajout.actif || occupe, reason: ajout.raison ?? undefined },
                          t.actif
                            ? { label: 'Retirer de la vente', icon: <PauseCircle size={15} aria-hidden="true" />, onClick: () => { void geste(t, 'retirer') }, disabled: !bascule.actif || occupe, reason: bascule.raison ?? undefined }
                            : { label: 'Remettre en vente', icon: <ArchiveRestore size={15} aria-hidden="true" />, onClick: () => { void geste(t, 'remettre') }, disabled: !bascule.actif || occupe, reason: bascule.raison ?? undefined },
                          { label: 'Supprimer', icon: <Trash2 size={15} aria-hidden="true" />, danger: true, onClick: () => { void geste(t, 'supprimer') }, disabled: !sup.actif || occupe, reason: sup.raison ?? undefined },
                        ]} />
                      </span>
                    ) : null}
                    {t.avertissements.length > 0 ? (
                      <ul className="bs-avertissements">{t.avertissements.map((a) => <li key={a.code}>{a.message}</li>)}</ul>
                    ) : null}
                  </article>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
      <TarifModal open={ouverte} edition={edition} ecrivain={ecrivain} tvaDefautBp={tvaDefautBp} onClose={() => setOuverte(false)} />
    </section>
  )
}

/* ═══════════════════════ la modale d'un tarif ═══════════════════════ */

/** Une édition ouverte : un tarif à modifier, ou un nouveau tarif — vierge, ou copie d'un autre (« Dupliquer »). */
interface Edition { tarifId: string | null; copieDe: string | null; tour: number }

function TarifModal(props: { open: boolean; edition: Edition | null; ecrivain: Ecrivain; tvaDefautBp: number | null; onClose: () => void }) {
  const dirty = useRef(false)
  const fermer = () => guardedClose(dirty.current, () => { dirty.current = false; props.onClose() })
  const e = props.edition
  const tarifs = props.ecrivain.soiree.tarifs
  const cible = e?.tarifId ? tarifs.find((t) => t.id === e.tarifId) ?? null : null
  const modeleCopie = e?.copieDe ? tarifs.find((t) => t.id === e.copieDe) ?? null : null
  return (
    <Modal open={props.open} onClose={fermer} ariaLabel={cible ? `Modifier « ${cible.nom} »` : modeleCopie ? `Dupliquer « ${modeleCopie.nom} »` : 'Ajouter un tarif'} width={720} className="bs-tf-modale">
      {!e ? null : e.tarifId !== null && !cible ? (
        <div className="bs-modale">
          <p className="bs-note" role="status">Ce tarif n’existe plus : il a été supprimé entre-temps.</p>
          <div className="bs-modale__actions"><button type="button" className="lc-btn" onClick={props.onClose}>Fermer</button></div>
        </div>
      ) : (
        <TarifEdition key={e.tour} ecrivain={props.ecrivain} cible={cible} depart={modeleCopie ? saisieCopieDe(modeleCopie) : null} tvaDefautBp={props.tvaDefautBp}
          onClose={() => { dirty.current = false; props.onClose() }} onDirty={(d) => { dirty.current = d }} />
      )}
    </Modal>
  )
}

function TarifEdition({ ecrivain, cible, depart, tvaDefautBp, onClose, onDirty }: {
  ecrivain: Ecrivain; cible: Tarif | null
  /** Le point de départ d'un nouveau tarif (copie) ; `null` : vierge. */
  depart: SaisieTarif | null
  tvaDefautBp: number | null; onClose: () => void; onDirty: (d: boolean) => void
}) {
  const { soiree: s, fiche } = ecrivain
  const { prefs } = usePreferencesBilletterie()
  const creation = cible === null
  const geste = creation ? s.gestes.ajouter_tarif : cible.gestes.modifier
  const bouton = etatBouton(creation ? 'ajouter_tarif' : 'modifier_tarif', geste, fiche.actions, fiche.ecriture)
  const [lu, setLu] = useState<SaisieTarif>(() => (cible ? saisieDepuisTarif(cible) : saisieVierge()))
  // Une copie part de la saisie du tarif copié : elle est déjà « à enregistrer », la barre l'offre aussitôt.
  const [saisie, setSaisie] = useState<SaisieTarif>(() => depart ?? lu)
  const [version, setVersion] = useState(cible?.version ?? 0)
  const [erreursServeur, setErreursServeur] = useState<ErreursTarif>({})
  const [ambiguites, setAmbiguites] = useState<Ambiguite[]>([])
  const [perimee, setPerimee] = useState(false)
  const [relire, setRelire] = useState(false)
  const [modele, setModele] = useState('')

  // « Recharger » après « modifié entre-temps » : on repart du tarif relu, une fois arrivé.
  useEffect(() => {
    if (!relire || !cible || cible.version === version) return
    const neuf = saisieDepuisTarif(cible)
    setLu(neuf); setSaisie(neuf); setVersion(cible.version)
    setPerimee(false); setRelire(false); setErreursServeur({}); setAmbiguites([])
  }, [relire, cible?.version]) // eslint-disable-line react-hooks/exhaustive-deps

  const mode = creation ? 'creation' : 'modification'
  const n = champsModifies(lu, saisie)
  const modifie = bouton.actif && n > 0
  useEffect(() => { onDirty(modifie) }, [modifie]) // eslint-disable-line react-hooks/exhaustive-deps
  const local = corpsTarif(saisie, mode)
  const erreursLocales: ErreursTarif = 'erreurs' in local ? local.erreurs : {}
  // Les erreurs d'un champ s'affichent dès qu'il a été touché ; toutes bloquent l'envoi.
  const visibles: ErreursTarif = { ...erreursServeur }
  for (const [k, msg] of Object.entries(erreursLocales) as [keyof SaisieTarif, string][]) {
    const a = lu[k]; const b = saisie[k]
    if (Array.isArray(a) && Array.isArray(b) ? a.join() !== b.join() : a !== b) visibles[k] = msg
  }
  const premiereErreur = Object.values(erreursLocales)[0] ?? null

  useSaveBar({
    dirty: modifie,
    count: n,
    saveLabel: creation ? 'Ajouter le tarif' : 'Enregistrer le tarif',
    invalid: premiereErreur,
    successMessage: creation ? 'Tarif ajouté.' : 'Tarif enregistré.',
    // Annuler une copie, c'est y renoncer : la fenêtre se ferme au lieu de montrer un tarif vierge.
    onCancel: () => { if (depart) { onClose(); return } setSaisie(lu); setErreursServeur({}); setAmbiguites([]) },
    onSave: async () => {
      if ('erreurs' in local) return Object.values(local.erreurs)[0] ?? 'Corrigez les champs signalés.'
      setErreursServeur({}); setAmbiguites([])
      const e = creation
        ? await ecrivain.executer({
          ecran: `ajout-tarif:${s.id}`, session: true, corps: local.corps, silencieux: true, succes: 'Tarif ajouté.',
          envoi: (cle) => ajouterTarif(s.id, local.corps, cle), preparationHorsLigne: !geste.publier_requis,
        })
        : await (async () => {
          const corps = { ...local.corps, version }
          return ecrivain.executer({
            ecran: `tarif:${cible.id}:modifier`, corps, silencieux: true, succes: 'Tarif enregistré.',
            envoi: (cle) => modifierTarif(s.id, cible.id, corps, cle), preparationHorsLigne: !geste.publier_requis,
          })
        })()
      // Enregistré : plus rien n'est « modifié », la barre se retire avec la modale.
      if (!e) { setLu(saisie); onClose(); return true }
      if (e.genre === 'heure_ambigue') setAmbiguites(e.ambiguites)
      if (e.genre === 'version') setPerimee(true)
      const parChamp: ErreursTarif = {}
      for (const c of e.champs) { const k = champDeLaSaisie(c); if (k) parChamp[k] = e.message }
      setErreursServeur(parChamp)
      return e.message
    },
  })

  const autresDuGroupe = s.tarifs.filter((t) => t.id !== cible?.id && t.cascade_groupe && t.cascade_groupe === saisie.cascade_groupe.trim())
  const rangSuivant = autresDuGroupe.some((t) => (t.cascade_rang ?? 0) > Number(saisie.cascade_rang))
  const avertissements = avertissementsSaisie(saisie, { rangSuivant })
  const cascade = creation ? phraseCascadeAjout(s.statut, saisie.cascade_groupe.trim() || null, saisie.cascade_rang.trim() ? Number(saisie.cascade_rang) : null) : null
  const sallesSoiree = s.salles.map((x) => ({ code: x.code, nom: x.nom }))
  const modeles = useMemo(() => prefs.modeles_tarifs.map((m) => ({ value: m.id, label: m.libelle })), [prefs.modeles_tarifs])

  const partirDuModele = (id: string) => {
    setModele(id)
    const m = prefs.modeles_tarifs.find((x) => x.id === id)
    if (!m) return
    setSaisie(tarifEffectif(tarifDepuisModele(m, m.id), s.nuit, s.ouverture_heure ?? ''))
  }

  const meta = creation
    ? `Pour « ${s.nom} »`
    : `${cible.compteurs.vendus.toLocaleString('fr-FR')} vendu${cible.compteurs.vendus > 1 ? 's' : ''}${cible.quota !== null ? ` sur ${cible.quota.toLocaleString('fr-FR')}` : ''} · ${CANAL_LIBELLE[cible.canal]}`

  return (
    <div className="bs-tf-fenetre">
      <header className="bs-tf-tete">
        <span className="bs-tf-tete__icone" aria-hidden="true"><Ticket size={20} /></span>
        <span className="bs-tf-tete__textes">
          <span className="bs-tf-tete__kicker">{creation ? (depart ? 'Dupliquer un tarif' : 'Nouveau tarif') : 'Modifier le tarif'}</span>
          <h2 className="bs-tf-tete__titre">{creation ? saisie.nom.trim() || 'Nouveau tarif' : cible.nom}</h2>
          <span className="bs-tf-tete__meta">{meta}</span>
        </span>
        <button type="button" className="lc-btn lc-btn--icon bs-tf-tete__fermer" aria-label="Fermer" onClick={() => guardedClose(modifie, onClose)}><X size={16} aria-hidden="true" /></button>
      </header>

      <div className="bs-tf-corps">
        {bouton.raison ? <p className="bs-note" role="status">{bouton.raison}</p> : null}
        {geste.publier_requis ? (
          <p className="bs-note bs-note--doute">
            <ShieldCheck size={14} aria-hidden="true" />{' '}
            {creation
              ? phraseAjoutEnLigne(s, saisie.canal)
              : 'Cette soirée est en ligne : ce changement agit tout de suite sur la vente. Gestion vous demandera de confirmer votre identité.'}
          </p>
        ) : null}
        {cascade ? <p className="bs-note bs-note--doute">{cascade}</p> : null}
        {creation && !depart && modeles.length > 0 && bouton.actif ? (
          <div className="lc-field bs-champ">
            <span className="lc-label">Partir d’un modèle (facultatif)</span>
            <Select value={modele} options={modeles} onChange={partirDuModele} placeholder="Tarif vierge" ariaLabel="Partir d’un modèle de tarif" />
            <span className="lc-helper">Les heures du modèle sont posées sur la nuit de cette soirée. Rien n’est enregistré avant « Ajouter le tarif ».</span>
          </div>
        ) : null}
        {perimee ? (
          <div className="bs-note bs-note--ko" role="alert">
            <span>Ce tarif a été modifié entre-temps. Rechargez-le pour voir la dernière version : vos changements seront perdus.</span>
            <button type="button" className="lc-btn lc-btn--sm" onClick={() => { setRelire(true); ecrivain.recharger() }}>Recharger le tarif</button>
          </div>
        ) : null}
        {ambiguites.length > 0 ? (
          <div className="bs-choix" role="group" aria-label="Préciser les heures">
            {ambiguites.map((a) => {
              const k = champDeLaSaisie(a.champ)
              return (
                <div key={a.champ} className="bs-choix__ligne">
                  <span>{champLisible(a.champ)} : cette heure existe deux fois cette nuit-là. Laquelle ?</span>
                  <span className="bs-choix__boutons">
                    {a.possibles.map((p) => (
                      <button key={p} type="button" className={`lc-btn lc-btn--sm${k && saisie[k] === p ? ' lc-btn--primary' : ''}`}
                        onClick={() => { if (k === 'vente_debut' || k === 'vente_fin') setSaisie({ ...saisie, [k]: p }) }}>{libelleChoixHeure(p)}</button>
                    ))}
                  </span>
                </div>
              )
            })}
          </div>
        ) : null}
        {!creation && cible.compteurs.emis === 0 ? <p className="lc-hint">{PHRASE_PREMIER_BILLET_TARIF}</p> : null}
        {creation ? <p className="lc-hint">{PHRASE_PREMIER_BILLET_TARIF}</p> : null}
        {!creation && cible.compteurs.emis > 0 ? <p className="lc-hint">{cible.compteurs.emis} billet{cible.compteurs.emis > 1 ? 's' : ''} émis sur ce tarif : ce qui est grisé ne se change plus.</p> : null}
        <TarifFenetreChamps
          valeur={saisie} onChange={(x) => { setSaisie(x); setErreursServeur({}) }} mode={mode} salles={sallesSoiree}
          cascades={cascadesARejoindre(s.tarifs, saisie.canal, cible?.id ?? null)}
          erreurs={visibles} verrous={creation ? [] : geste.verrous} quotaMin={creation ? null : geste.quota_min}
          raisonsVerrous={creation ? undefined : raisonsVerrousTarif(cible, s)}
          tvaDefaut={creation && tvaDefautBp !== null ? tvaLisible(tvaDefautBp) : null}
          complet={!creation} lectureSeule={!bouton.actif}
        />
        {avertissements.length > 0 ? <ul className="bs-avertissements">{avertissements.map((a) => <li key={a}>{a}</li>)}</ul> : null}
      </div>

      <footer className="bs-tf-pied">
        <span className="bs-tf-pied__aide">{bouton.actif ? 'On enregistre avec la barre en bas de l’écran.' : 'Lecture seule.'}</span>
        <button type="button" className="lc-btn lc-btn--ghost" onClick={() => guardedClose(modifie, onClose)}>Fermer</button>
      </footer>
    </div>
  )
}
